import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import firebaseService from '../services/firebaseService';
import toast from 'react-hot-toast';
import './GameConfig.css';

const BASE_POINT_LABELS = {
  review_restroom: 'Review Restroom',
  add_restroom: 'Add Restroom',
  restroom_verified: 'Restroom Verified',
  restroom_shared: 'Restroom Shared',
  review_liked: 'Review Received a Like',
  like_given_unique: 'Like Given (unique user)',
  referral: 'Referral'
};

function rowsFromObject(obj) {
  return Object.entries(obj || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([threshold, bonus]) => ({ threshold, bonus: String(bonus) }));
}

function rulesToForm(rules) {
  return {
    basePoints: Object.fromEntries(
      Object.entries(rules.basePoints || {}).map(([key, value]) => [key, String(value)])
    ),
    reviewsMilestones: rowsFromObject(rules.milestoneBonuses?.reviews),
    additionsMilestones: rowsFromObject(rules.milestoneBonuses?.additions),
    titles: (rules.titles || []).map((title) => ({
      minTP: String(title.minTP),
      name: title.name
    })),
    categories: Object.entries(rules.badges?.categories || {}).map(([key, thresholds]) => ({
      key,
      thresholds: thresholds.join(', ')
    }))
  };
}

function formToRules(form) {
  const rowsToObject = (rows) => {
    const obj = {};
    rows.forEach((row) => {
      const threshold = Number(row.threshold);
      const bonus = Number(row.bonus);
      if (Number.isFinite(threshold) && Number.isFinite(bonus) && row.threshold !== '') {
        obj[String(threshold)] = bonus;
      }
    });
    return obj;
  };

  const parseThresholds = (str) =>
    String(str)
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((value) => Number.isFinite(value) && value > 0);

  return {
    basePoints: Object.fromEntries(
      Object.entries(form.basePoints).map(([key, value]) => [key, Number(value) || 0])
    ),
    milestoneBonuses: {
      reviews: rowsToObject(form.reviewsMilestones),
      additions: rowsToObject(form.additionsMilestones)
    },
    titles: form.titles
      .map((title) => ({ minTP: Number(title.minTP), name: title.name.trim() }))
      .filter((title) => Number.isFinite(title.minTP) && title.name),
    badges: {
      categories: Object.fromEntries(
        form.categories
          .filter((category) => category.key.trim())
          .map((category) => [category.key.trim(), parseThresholds(category.thresholds)])
      )
    }
  };
}

function GameConfig() {
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);

  const databaseIdHash = localStorage.getItem('database_id_hash') || '';

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRules = async () => {
    setLoading(true);
    setError('');
    try {
      const functions = getFunctions(firebaseService.app);
      const getRules = httpsCallable(functions, 'GetGamificationRules');
      const result = await getRules({ database_id_hash: databaseIdHash });

      if (result.data?.success && result.data.rules) {
        setRules(result.data.rules);
        setForm(rulesToForm(result.data.rules));
        setDirty(false);
      } else {
        setError(result.data?.validationError || 'Failed to load gamification rules.');
      }
    } catch (loadError) {
      console.error('Error loading gamification rules:', loadError);
      setError(loadError.message || 'Failed to load gamification rules.');
    } finally {
      setLoading(false);
    }
  };

  const saveRules = async () => {
    if (!form) return;
    setSaving(true);
    setError('');
    try {
      const functions = getFunctions(firebaseService.app);
      const saveFn = httpsCallable(functions, 'SaveGamificationRules');
      const result = await saveFn({
        rules: formToRules(form),
        database_id_hash: databaseIdHash
      });

      if (result.data?.success) {
        toast.success(result.data.message || 'Gamification rules saved.');
        setDirty(false);
        const getRules = httpsCallable(functions, 'GetGamificationRules');
        const fresh = await getRules({ database_id_hash: databaseIdHash });
        if (fresh.data?.success && fresh.data.rules) {
          setRules(fresh.data.rules);
          setForm(rulesToForm(fresh.data.rules));
        }
      } else {
        toast.error('Failed to save gamification rules.');
      }
    } catch (saveError) {
      console.error('Error saving gamification rules:', saveError);
      const message = saveError.message || 'Failed to save gamification rules.';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const updateForm = (updater) => {
    setForm((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      return next;
    });
    setDirty(true);
  };

  if (loading) {
    return <div className="game-config-message">Loading gamification rules...</div>;
  }

  if (error && !form) {
    return (
      <div className="game-config-message">
        <p>{error}</p>
        <button className="btn btn-secondary btn-sm" onClick={loadRules}>
          Retry
        </button>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="game-config-message">
        <p>No gamification rules loaded.</p>
        <button className="btn btn-secondary btn-sm" onClick={loadRules}>
          Reload
        </button>
      </div>
    );
  }

  return (
    <div className="game-config">
      <div className="tab-header">
        <h2>🎮 Game Config</h2>
        <p>Edit TP points distribution and badge thresholds.</p>
      </div>

      <div className="game-config-toolbar">
        {dirty && <span className="game-config-dirty">Unsaved changes</span>}
        <button className="btn btn-secondary btn-sm" onClick={loadRules} disabled={saving}>
          ⟳ Reload
        </button>
        <button className="btn btn-primary btn-sm" onClick={saveRules} disabled={saving}>
          {saving ? '💾 Saving...' : '💾 Save Changes'}
        </button>
      </div>

      {error && <div className="game-config-error">{error}</div>}

      <div className="settings-section">
        <div className="section-header">
          <h3>💰 TP Points Distribution</h3>
          <p className="section-description">Base points awarded for each action.</p>
        </div>
        <table className="game-config-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>TP Points</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(form.basePoints).map(([key, value]) => (
              <tr key={key}>
                <td>{BASE_POINT_LABELS[key] || key}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="game-config-input game-config-input-sm"
                    value={value}
                    onChange={(e) =>
                      updateForm((prev) => ({
                        ...prev,
                        basePoints: { ...prev.basePoints, [key]: e.target.value }
                      }))
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <h3>🏅 Milestone Bonuses</h3>
          <p className="section-description">
            One-time bonus TP awarded when a user crosses a review or add milestone.
          </p>
        </div>

        <div className="game-config-columns">
          <div>
            <h4>Reviews</h4>
            <table className="game-config-table">
              <thead>
                <tr>
                  <th>Milestone</th>
                  <th>Bonus TP</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {form.reviewsMilestones.map((row, index) => (
                  <tr key={`reviews-${index}`}>
                    <td>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className="game-config-input game-config-input-sm"
                        value={row.threshold}
                        onChange={(e) =>
                          updateForm((prev) => {
                            const next = [...prev.reviewsMilestones];
                            next[index] = { ...next[index], threshold: e.target.value };
                            return { ...prev, reviewsMilestones: next };
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="game-config-input game-config-input-sm"
                        value={row.bonus}
                        onChange={(e) =>
                          updateForm((prev) => {
                            const next = [...prev.reviewsMilestones];
                            next[index] = { ...next[index], bonus: e.target.value };
                            return { ...prev, reviewsMilestones: next };
                          })
                        }
                      />
                    </td>
                    <td>
                      <button
                        className="game-config-remove"
                        title="Remove milestone"
                        onClick={() =>
                          updateForm((prev) => ({
                            ...prev,
                            reviewsMilestones: prev.reviewsMilestones.filter((_, i) => i !== index)
                          }))
                        }
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() =>
                updateForm((prev) => ({
                  ...prev,
                  reviewsMilestones: [...prev.reviewsMilestones, { threshold: '', bonus: '0' }]
                }))
              }
            >
              ➕ Add Review Milestone
            </button>
          </div>

          <div>
            <h4>Restroom Additions</h4>
            <table className="game-config-table">
              <thead>
                <tr>
                  <th>Milestone</th>
                  <th>Bonus TP</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {form.additionsMilestones.map((row, index) => (
                  <tr key={`additions-${index}`}>
                    <td>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className="game-config-input game-config-input-sm"
                        value={row.threshold}
                        onChange={(e) =>
                          updateForm((prev) => {
                            const next = [...prev.additionsMilestones];
                            next[index] = { ...next[index], threshold: e.target.value };
                            return { ...prev, additionsMilestones: next };
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="game-config-input game-config-input-sm"
                        value={row.bonus}
                        onChange={(e) =>
                          updateForm((prev) => {
                            const next = [...prev.additionsMilestones];
                            next[index] = { ...next[index], bonus: e.target.value };
                            return { ...prev, additionsMilestones: next };
                          })
                        }
                      />
                    </td>
                    <td>
                      <button
                        className="game-config-remove"
                        title="Remove milestone"
                        onClick={() =>
                          updateForm((prev) => ({
                            ...prev,
                            additionsMilestones: prev.additionsMilestones.filter((_, i) => i !== index)
                          }))
                        }
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() =>
                updateForm((prev) => ({
                  ...prev,
                  additionsMilestones: [...prev.additionsMilestones, { threshold: '', bonus: '0' }]
                }))
              }
            >
              ➕ Add Addition Milestone
            </button>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <h3>👑 Titles</h3>
          <p className="section-description">
            Rank titles by minimum TP. Must be strictly ascending by TP.
          </p>
        </div>
        <table className="game-config-table">
          <thead>
            <tr>
              <th>Min TP</th>
              <th>Title</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {form.titles.map((title, index) => (
              <tr key={`title-${index}`}>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="game-config-input game-config-input-sm"
                    value={title.minTP}
                    onChange={(e) =>
                      updateForm((prev) => {
                        const next = [...prev.titles];
                        next[index] = { ...next[index], minTP: e.target.value };
                        return { ...prev, titles: next };
                      })
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="game-config-input"
                    value={title.name}
                    onChange={(e) =>
                      updateForm((prev) => {
                        const next = [...prev.titles];
                        next[index] = { ...next[index], name: e.target.value };
                        return { ...prev, titles: next };
                      })
                    }
                  />
                </td>
                <td>
                  <button
                    className="game-config-remove"
                    title="Remove title"
                    onClick={() =>
                      updateForm((prev) => ({
                        ...prev,
                        titles: prev.titles.filter((_, i) => i !== index)
                      }))
                    }
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() =>
            updateForm((prev) => ({
              ...prev,
              titles: [...prev.titles, { minTP: '', name: '' }]
            }))
          }
        >
          ➕ Add Title
        </button>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <h3>🎖️ Badges</h3>
          <p className="section-description">
            Thresholds are comma-separated positive integers, strictly ascending.
          </p>
        </div>

        <h4>Category Badges</h4>
        <table className="game-config-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Thresholds</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {form.categories.map((category, index) => (
              <tr key={`category-${index}`}>
                <td>
                  <input
                    type="text"
                    className="game-config-input"
                    value={category.key}
                    onChange={(e) =>
                      updateForm((prev) => {
                        const next = [...prev.categories];
                        next[index] = { ...next[index], key: e.target.value };
                        return { ...prev, categories: next };
                      })
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="game-config-input"
                    value={category.thresholds}
                    onChange={(e) =>
                      updateForm((prev) => {
                        const next = [...prev.categories];
                        next[index] = { ...next[index], thresholds: e.target.value };
                        return { ...prev, categories: next };
                      })
                    }
                  />
                </td>
                <td>
                  <button
                    className="game-config-remove"
                    title="Remove category badge"
                    onClick={() =>
                      updateForm((prev) => ({
                        ...prev,
                        categories: prev.categories.filter((_, i) => i !== index)
                      }))
                    }
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() =>
            updateForm((prev) => ({
              ...prev,
              categories: [...prev.categories, { key: '', thresholds: '1, 10, 25' }]
            }))
          }
        >
          ➕ Add Category Badge
        </button>
      </div>

      <div className="game-config-toolbar">
        {dirty && <span className="game-config-dirty">Unsaved changes</span>}
        <button className="btn btn-secondary btn-sm" onClick={loadRules} disabled={saving}>
          ⟳ Reload
        </button>
        <button className="btn btn-primary btn-sm" onClick={saveRules} disabled={saving}>
          {saving ? '💾 Saving...' : '💾 Save Changes'}
        </button>
      </div>
    </div>
  );
}

export default GameConfig;
